/**
 * FERP Portal - Rule Detail Drawer (DRR-058)
 *
 * Right-side drawer showing full regulatory rule detail
 * with approve/reject actions for the HITL review workflow.
 *
 * Pattern source: JournalEntryApprovePage (approve/reject buttons)
 */

import { useState } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Badge,
  Text,
  Title,
  Paper,
  Grid,
  Progress,
  Button,
  Textarea,
  Divider,
  Alert,
  List,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconShieldCheck,
  IconAlertTriangle,
  IconClock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/auth';
import {
  useApproveRegulatoryRuleMutation,
  useRejectRegulatoryRuleMutation,
  GqlRuleStatus,
  GqlReviewQueue,
  GqlSafetyLevel,
  type RegulatoryRuleFieldsFragment,
} from '@/generated/graphql';

// ============================================================================
// Constants
// ============================================================================

const SAFETY_COLORS: Record<string, string> = {
  [GqlSafetyLevel.CRITICAL]: 'red',
  [GqlSafetyLevel.HIGH]: 'orange',
  [GqlSafetyLevel.STANDARD]: 'blue',
  [GqlSafetyLevel.LOW]: 'gray',
};

const STATUS_COLORS: Record<string, string> = {
  [GqlRuleStatus.PENDING_REVIEW]: 'yellow',
  [GqlRuleStatus.IN_REVIEW]: 'blue',
  [GqlRuleStatus.APPROVED]: 'green',
  [GqlRuleStatus.REJECTED]: 'red',
  [GqlRuleStatus.SUPERSEDED]: 'gray',
  [GqlRuleStatus.EXPIRED]: 'gray',
};

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.7) return 'yellow';
  return 'red';
}

// ============================================================================
// Types
// ============================================================================

interface RuleDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  rule: RegulatoryRuleFieldsFragment | null;
  tenantId: string;
  onActionComplete?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function RuleDetailDrawer({ opened, onClose, rule, tenantId, onActionComplete }: RuleDetailDrawerProps) {
  const { user } = useAuth();
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approveComment, setApproveComment] = useState('');

  const [approveRule, { loading: approving }] = useApproveRegulatoryRuleMutation();
  const [rejectRule, { loading: rejectLoading }] = useRejectRegulatoryRuleMutation();

  if (!rule) return null;

  const isReviewable = rule.status === GqlRuleStatus.PENDING_REVIEW || rule.status === GqlRuleStatus.IN_REVIEW;
  const confidencePercent = Math.round(rule.extraction.confidence * 100);

  const handleApprove = async () => {
    if (!user?.id) return;

    try {
      await approveRule({
        variables: {
          input: {
            tenantId,
            ruleId: rule.id,
            reviewerId: user.id,
            comment: approveComment || undefined,
          },
        },
      });

      notifications.show({
        title: 'Rule approved',
        message: `${rule.id} has been approved and will be enforced.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      setApproveComment('');
      onActionComplete?.();
      onClose();
    } catch (err) {
      notifications.show({
        title: 'Approval failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const handleReject = async () => {
    if (!user?.id) return;

    if (!rejectionReason.trim()) {
      notifications.show({
        title: 'Reason required',
        message: 'Please provide a reason for rejection.',
        color: 'yellow',
      });
      return;
    }

    try {
      await rejectRule({
        variables: {
          input: {
            tenantId,
            ruleId: rule.id,
            reviewerId: user.id,
            reason: rejectionReason,
          },
        },
      });

      notifications.show({
        title: 'Rule rejected',
        message: `${rule.id} has been rejected.`,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setRejecting(false);
      setRejectionReason('');
      onActionComplete?.();
      onClose();
    } catch (err) {
      notifications.show({
        title: 'Rejection failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="sm">
          <Title order={4}>{rule.id}</Title>
          <Badge size="sm" variant="light">v{rule.version}</Badge>
        </Group>
      }
    >
      <Stack gap="md">
        {/* Status Badges */}
        <Group gap="xs">
          <Badge color={STATUS_COLORS[rule.status] ?? 'gray'} variant="light">
            {rule.status.replace('_', ' ')}
          </Badge>
          <Badge color={SAFETY_COLORS[rule.classification.safetyLevel] ?? 'gray'} variant="filled">
            {rule.classification.safetyLevel}
          </Badge>
          <Badge
            color={rule.queue === GqlReviewQueue.SAFETY ? 'red' : rule.queue === GqlReviewQueue.AUTO ? 'green' : 'yellow'}
            variant="outline"
          >
            {rule.queue} Queue
          </Badge>
        </Group>

        {/* Life-critical warning */}
        {rule.classification.safetyLevel === GqlSafetyLevel.CRITICAL && (
          <Alert color="red" icon={<IconShieldCheck size={16} />} title="Life-Critical Rule">
            <Text size="sm">
              This rule involves patient safety. It always requires human review
              regardless of confidence score.
            </Text>
          </Alert>
        )}

        <Divider />

        {/* Original Text */}
        <div>
          <Text size="sm" fw={600} mb={4}>Original Text</Text>
          <Paper withBorder p="sm" bg="gray.0">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {rule.text}
            </Text>
          </Paper>
        </div>

        {/* Structured Rule */}
        <div>
          <Text size="sm" fw={600} mb={4}>Structured Rule</Text>
          <Grid gutter="xs">
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">WHO (Actor)</Text>
              <Text size="sm" fw={500}>{rule.structured.actor}</Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">WHAT (Action)</Text>
              <Text size="sm" fw={500}>{rule.structured.action}</Text>
            </Grid.Col>
            {rule.structured.trigger && (
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">WHEN (Trigger)</Text>
                <Text size="sm" fw={500}>{rule.structured.trigger}</Text>
              </Grid.Col>
            )}
            {rule.structured.deadline && (
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">DEADLINE</Text>
                <Group gap={4}>
                  <IconClock size={14} />
                  <Text size="sm" fw={500}>{rule.structured.deadline}</Text>
                </Group>
              </Grid.Col>
            )}
          </Grid>
        </div>

        {/* Evidence Requirements */}
        {rule.structured.evidence.length > 0 && (
          <div>
            <Text size="sm" fw={600} mb={4}>Evidence Required</Text>
            <List size="sm" spacing="xs">
              {rule.structured.evidence.map((e, i) => (
                <List.Item key={i}>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{e.name}</Text>
                    {e.required && <Badge size="xs" color="red" variant="light">required</Badge>}
                    {e.fhirResource && <Badge size="xs" variant="outline">{e.fhirResource}</Badge>}
                  </Group>
                  <Text size="xs" c="dimmed">{e.description}</Text>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        {/* Exceptions */}
        {rule.structured.exceptions.length > 0 && (
          <div>
            <Text size="sm" fw={600} mb={4}>Exceptions</Text>
            <List size="sm" spacing="xs">
              {rule.structured.exceptions.map((e, i) => (
                <List.Item key={i}>
                  <Text size="sm">{e.condition}</Text>
                  {e.alternativeAction && (
                    <Text size="xs" c="dimmed">Alternative: {e.alternativeAction}</Text>
                  )}
                </List.Item>
              ))}
            </List>
          </div>
        )}

        <Divider />

        {/* Source Citation */}
        <div>
          <Text size="sm" fw={600} mb={4}>Source</Text>
          <Text size="sm">{rule.source.document.title}</Text>
          <Text size="xs" c="dimmed">
            {rule.source.location.section}
            {rule.source.location.subdivision ? `, ${rule.source.location.subdivision}` : ''}
            {rule.source.location.page ? `, p.${rule.source.location.page}` : ''}
          </Text>
          {rule.source.effectiveDate && (
            <Text size="xs" c="dimmed">Effective: {rule.source.effectiveDate}</Text>
          )}
        </div>

        {/* Extraction Metadata */}
        <div>
          <Text size="sm" fw={600} mb={4}>Extraction Confidence</Text>
          <Group gap="sm">
            <Progress
              value={confidencePercent}
              color={getConfidenceColor(rule.extraction.confidence)}
              size="lg"
              style={{ flex: 1 }}
            />
            <Text size="sm" fw={600}>{confidencePercent}%</Text>
          </Group>
          {rule.extraction.confidence < 0.9 && (
            <Text size="xs" c="dimmed" mt={2}>
              Below 90% auto-approve threshold
            </Text>
          )}
          {rule.extraction.extractor && (
            <Text size="xs" c="dimmed" mt={2}>
              Extracted by: {rule.extraction.extractor}
            </Text>
          )}
        </div>

        {/* Conflicts */}
        {rule.conflictingRuleIds.length > 0 && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Conflicts Detected">
            <Text size="sm">
              This rule may conflict with: {rule.conflictingRuleIds.join(', ')}
            </Text>
          </Alert>
        )}

        {/* Supersedes */}
        {rule.supersedes && (
          <Text size="xs" c="dimmed">Supersedes: {rule.supersedes}</Text>
        )}

        {/* Changelog */}
        {rule.changelog && (
          <div>
            <Text size="sm" fw={600} mb={4}>Changelog</Text>
            <Text size="xs" c="dimmed">{rule.changelog}</Text>
          </div>
        )}

        {/* Previous rejection */}
        {rule.status === GqlRuleStatus.REJECTED && rule.rejectionReason && (
          <Alert color="red" icon={<IconX size={16} />} title="Rejected">
            <Text size="sm">{rule.rejectionReason}</Text>
            {rule.reviewerId && (
              <Text size="xs" c="dimmed" mt={4}>By: {rule.reviewerId}</Text>
            )}
          </Alert>
        )}

        {/* Previous approval */}
        {rule.status === GqlRuleStatus.APPROVED && (
          <Alert color="green" icon={<IconCheck size={16} />} title="Approved">
            {rule.reviewComment && <Text size="sm">{rule.reviewComment}</Text>}
            {rule.reviewerId && (
              <Text size="xs" c="dimmed" mt={4}>By: {rule.reviewerId}</Text>
            )}
          </Alert>
        )}

        <Divider />

        {/* Action Buttons */}
        {isReviewable && (
          <Stack gap="sm">
            {!rejecting ? (
              <>
                <Textarea
                  label="Comment (optional)"
                  placeholder="Add a review comment..."
                  value={approveComment}
                  onChange={(e) => setApproveComment(e.currentTarget.value)}
                  autosize
                  minRows={2}
                />
                <Group justify="flex-end">
                  <Button
                    variant="outline"
                    color="red"
                    leftSection={<IconX size={16} />}
                    onClick={() => setRejecting(true)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="filled"
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                    loading={approving}
                  >
                    Approve
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Textarea
                  label="Rejection Reason"
                  description="Required - explain why this rule is being rejected"
                  placeholder="This rule conflicts with..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.currentTarget.value)}
                  autosize
                  minRows={3}
                  required
                />
                <Group justify="flex-end">
                  <Button variant="subtle" onClick={() => setRejecting(false)}>
                    Cancel
                  </Button>
                  <Button
                    color="red"
                    leftSection={<IconX size={16} />}
                    onClick={handleReject}
                    loading={rejectLoading}
                  >
                    Confirm Rejection
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}

export default RuleDetailDrawer;
